/* eslint-disable no-console */

import angular from 'angular'
import VendNumber from 'vend-number'
import { datadogLogs } from '@datadog/browser-logs'
import { ApiSale } from '@vend/types'
import { WEB_REGISTER } from '../../components/web-register.constant'

import { SALE_VERSION } from '../../components/sale/sale-model.constant'
import { $injector } from '../../ng-compat/imports'
import { DatabaseService } from '../database/database'
import { SalesStore } from '../database/sales-store'

type DatadogLogs = typeof datadogLogs
declare let DD_LOGS: DatadogLogs

declare global {
  interface Window {
    __wrDebugTools: WrBrowserDebugTools
  }
}

const WR_ENABLE_DEBUG_LOGGING = 'WR_ENABLE_DEBUG_LOGGING!'

/**
 * Enables registering modules to be accessible via the browser console.
 */
export class WrDebugToolsModuleRegistry {
  private whitelistedModules: string[] = []
  private addedModules: Record<string, any> = {}

  /**
   * Register a module to be accessible via the browser console.
   *
   * For modules registered with the AngularJS DI system, only the module name should be passed in.
   * For modules not registered with the AngularJS DI system, the module name and the module itself should be passed in.
   */
  public register(moduleName: string, module?: any) {
    if (module) {
      this.addedModules[moduleName] = module
    } else {
      this.whitelistedModules.push(moduleName)
    }
  }

  /**
   * Get a module previously registered with the debug tools
   */
  public get(moduleName: string) {
    if (this.addedModules[moduleName]) {
      DD_LOGS.logger.debug('wr_debug_tools_get_manager', {
        module: moduleName,
      })
      console.warn(
        'This tooling is strictly for use by Vend engineers, use at your own risk.'
      )
      return this.addedModules[moduleName]
    }

    if (this.whitelistedModules.includes(moduleName)) {
      DD_LOGS.logger.debug('wr_debug_tools_get_manager', {
        module: moduleName,
      })
      console.warn(
        'This tooling is strictly for use by Vend engineers, use at your own risk.'
      )
      return $injector.get(moduleName)
    }

    console.error(
      `${moduleName} either doesn't exist or is not accessible via the browser console.`
    )
    return null
  }
}

/**
 * Enables registering modules to be accessible via the browser console.
 */
export const wrDebugToolsModuleRegistry = new WrDebugToolsModuleRegistry()

class WrDebugTools {
  /**
   * Register a module to be accessible via the browser console.
   *
   * For modules registered with the AngularJS DI system, only the module name should be passed in.
   * For modules not registered with the AngularJS DI system, the module name and the module itself should be passed in.
   */
  public register(moduleName: string, module?: any) {
    wrDebugToolsModuleRegistry.register(moduleName, module)
  }

  /**
   * Get the prefix used to enable debug logging.
   */
  public getEnableDebugLoggingPrefix() {
    return WR_ENABLE_DEBUG_LOGGING
  }
}

/**
 * Browser debug tools for WebRegister.
 */
export class WrBrowserDebugTools {
  public VendNumber = VendNumber

  /**
   * Reload the page with debug level logging and other debug information provided by angular enabled.
   */
  // @note relies on the debug-config module
  public reloadWithDebugInfo() {
    window.name = `${WR_ENABLE_DEBUG_LOGGING}${window.name}`
    angular.reloadWithDebugInfo()
  }

  /**
   * Get the instance of the specified module in the browser console.
   */
  public get(moduleName: string) {
    return wrDebugToolsModuleRegistry.get(moduleName)
  }

  /**
   * Log the collection of unsynced sales to the console so they can be further inspected to determine why they
   * might not be syncing.
   */
  public logUnsyncedSales() {
    $injector
      .get<SalesStore>('salesStore')
      .getUnsyncedSales()
      .then(unsyncedSales => console.log(unsyncedSales))
      .catch(error =>
        console.error('Error occurred listing unsynced sales', error)
      )
  }

  /**
   * Log the versions of all bundled Vend dependencies. Also includes the current web register version and
   * sale model version.
   *
   * @method versions
   *
   * @return {Object} Key value pairs of the bundled Vend dependencies.
   */
  public versions() {
    return {
      CLIENT_VERSION: WEB_REGISTER.CLIENT_VERSION,
      SALE_VERSION,
    }
  }

  /**
   * Log to the console all sales in IndexedDB that have the given receipt number.
   */
  public logSalesByReceiptNumber(receiptNumber: number) {
    const salesFound: ApiSale[] = []
    function iterator(cursor: $TSFixMe) {
      const sale = cursor.value
      if (
        sale.receiptNumberDetails &&
        sale.receiptNumberDetails.number === receiptNumber
      ) {
        salesFound.push(sale)
      }
      return true
    }
    ;(this.get('Database') as DatabaseService)
      .store('sales')
      .cursor({ iterator })
      .catch(error => {
        console.error(
          `Error occurred searching for sales with receipt #${receiptNumber}`,
          error
        )
      })
      .finally(() => {
        console.log(
          `${salesFound.length} sales found with receipt #${receiptNumber}`
        )
        if (salesFound.length) {
          console.log(salesFound)
        }
      })
  }

  /**
   * Export a Sale as JSON, printing the stringified JSON format of the Sale to the console.
   */
  public exportSaleAsJson(saleId: string) {
    $injector
      .get<SalesStore>('salesStore')
      .loadSaleJson(saleId)
      .then(sale => console.log(JSON.stringify(sale)))
  }
}

/**
 * AngularJS provider to access the debug tools via dependency injection.
 */
export class WrDebugToolsProvider {
  public getEnableDebugLoggingPrefix() {
    return WR_ENABLE_DEBUG_LOGGING
  }

  public $get() {
    return new WrDebugTools()
  }
}

window.__wrDebugTools = new WrBrowserDebugTools()

angular
  .module('webregister.factories')
  .provider('wrDebugTools', WrDebugToolsProvider)
